import { useGlobal } from '@utmb/contexts/GlobalContext';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Image } from '@utmb/design-system/image/Image';
import { Link } from '@utmb/design-system/link/link';
import { Font } from '@utmb/design-system/text/font';
import { ColorTypesEnum } from '@utmb/types/colors';
import { LinkTypeEnum } from '@utmb/types/enum/LinkTypeEnum';
import { IconsEnum } from '@utmb/types/icons';
import SliceExpandableCardsCollectionCardView from '@utmb/types/view/SliceExpandableCardsCollectionCardView';
import { getColorStyle } from '@utmb/utils/colors';
import { parseMarkdown } from '@utmb/utils/markdown';
import { ReactElement } from 'react';
import styles from './expandable-cards-collection-modal.module.scss';

interface ExpandableCardsCollectionModalProps {
    slices: SliceExpandableCardsCollectionCardView[];
    index: number;
    changeSlice: (index: number) => void;
    onClose: () => void;
}

const ExpandableCardsCollectionModal = (props: ExpandableCardsCollectionModalProps): ReactElement => {
    const { onClose, changeSlice, index, slices } = props;
    const card = slices[index];

    const {
        global: {
            event: { primaryColorIsDark },
        },
    } = useGlobal();

    const goToPrevious = (): void => changeSlice(index <= 0 ? slices.length - 1 : index - 1);
    const goToNext = (): void => changeSlice(index >= slices.length - 1 ? 0 : index + 1);

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div className={styles.overlay} role="dialog" onClick={onClose}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <button className={styles.close} onClick={onClose}>
                    <Icon variant={IconsEnum.CLOSE} clss="color-secondary" />
                </button>

                {card.picture && (
                    <div className={styles.picture}>
                        <Image image={card.picture} layout="fill" objectFit="cover" />
                    </div>
                )}

                <div className={styles.body}>
                    <Font.FuturaBold className={styles.title} desktop="30" mobile="24" as="h1">
                        {card.title}
                    </Font.FuturaBold>

                    {card.subtitle && (
                        <Font.FuturaHeavy className={styles.subtitle} desktop="24" mobile="20" as="h2">
                            {card.subtitle}
                        </Font.FuturaHeavy>
                    )}

                    <div className={styles.scrollable}>
                        {card.content && (
                            <Font.FuturaMedium desktop="20" mobile="16">
                                <div
                                    className={`markdown ${primaryColorIsDark ? 'primaryColorIsDark' : ''}`}
                                    dangerouslySetInnerHTML={{ __html: parseMarkdown(card.content) }}
                                />
                            </Font.FuturaMedium>
                        )}
                    </div>

                    <div className={styles.footer}>
                        <div>
                            {card.link &&
                                (card.link.type === LinkTypeEnum.BUTTON ? (
                                    <Link.Button href={card.link.slug} label={card.link.label} followClassName={`${card.link.followClass}-ga`} />
                                ) : (
                                    <Font.FuturaBook mobile="14">
                                        <Link className="font-underline" href={card.link.slug} followClassName={`${card.link.followClass}-ga`}>
                                            {card.link.label}
                                        </Link>
                                    </Font.FuturaBook>
                                ))}
                        </div>

                        <div className={styles.nav}>
                            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                            <div onClick={goToPrevious} role="button" tabIndex={0}>
                                <Icon variant={IconsEnum.LEFT} color={getColorStyle(ColorTypesEnum.PRIMARY)} />
                            </div>
                            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                            <div onClick={goToNext} role="button" tabIndex={0}>
                                <Icon variant={IconsEnum.RIGHT} color={getColorStyle(ColorTypesEnum.PRIMARY)} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpandableCardsCollectionModal;
